<template>
  <div>
    <h2 class="text-base mb-3 flex">
      <icon
        icon="order-horizontal"
        class="w-6 h-6 icons-primary mr-2"
      />
      Make a claim for {{ currentClaimDate }}
    </h2>

    <form
      action="/"
      class="bg-white shadow-lg mb-12 relative"
      @submit.prevent="sendRequest"
    >
      <div
        v-if="locked"
        class="absolute w-full h-full pin-t pin-l flex flex-col justify-center items-center z-20 bg-grey-900-70 pb-24 px-12 text-center text-white"
      >
        <icon
          icon="time"
          class="w-24 h-24 icons-primary mb-6"
        />
        <h2>Your next claim window opens {{ claimWindowDate }}</h2>
      </div>
      <div
        :class="{
          'opacity-50 bg-grey-100': locked || loading || zeroClaim
        }"
      >
        <claims-calculator-row
          v-model.number="expenditure.labour_costs"
          :details="expenditureInfo('labour_costs')"
          :disabled="locked || loading || zeroClaim"
        />
        <claims-calculator-row
          v-model.number="expenditure.overheads"
          :details="expenditureInfo('overheads')"
          :error="validateOverheads"
          :disabled="locked || loading || zeroClaim"
        />
        <claims-calculator-row
          v-model.number="expenditure.travel_subsistence"
          :details="expenditureInfo('travel_subsistence')"
          :disabled="locked || loading || zeroClaim"
        />
        <claims-calculator-row
          v-model.number="expenditure.software"
          :details="expenditureInfo('software')"
          :disabled="locked || loading || zeroClaim"
        />
        <claims-calculator-row
          v-model.number="expenditure.capital_equipment"
          :details="expenditureInfo('capital_equipment')"
        />
        <claims-calculator-row
          v-model.number="expenditure.materials"
          :details="expenditureInfo('materials')"
          :disabled="locked || loading || zeroClaim"
        />
        <claims-calculator-row
          v-model.number="expenditure.sub_contracts"
          :details="expenditureInfo('sub_contracts')"
          :error="validateSubContracts"
          :disabled="locked || loading || zeroClaim"
        />
        <div
          class="-mt-px text-sm flex flex-wrap border-t-2 font-bold bg-grey-100"
        >
          <div class="w-2/3 p-3 border-r text-right">
            Expenditure Total
          </div>
          <div class="w-1/3 px-3 py-3 flex text-right">
            {{ total | toPounds }}
          </div>
        </div>
        <div
          class="flex flex-wrap border-b border-t font-bold text-lg"
          :class="{
            'bg-primary-100 border-primary-200': !overclaimed,
            'bg-negative-100 border-negative-200 text-negative-800': overclaimed
          }"
        >
          <div
            class="w-2/3 p-3 border-r text-right"
            :class="{
              'border-primary-200': !overclaimed,
              'border-negative-200': overclaimed
            }"
          >
            Your Claim
          </div>
          <div class="w-1/3 px-3 py-3 flex">
            {{ grantRequest | toPounds }}
          </div>
          <div
            v-if="overclaimed"
            class="w-full px-3 py-2 text-2xs text-negative-100 bg-negative-600 text-right"
          >
            <em>
              You only have {{ remaining | toPounds }} of your contracted grant
              amount remaining.
            </em>
          </div>
        </div>
        <div class="flex flex-wrap font-bold bg-grey-800 text-white">
          <div class="w-2/3 p-3 border-r border-grey-900 text-right">
            Upload finance form
            <small class="font-normal italic block leading-tight">
              Please upload your supporting financial information.
            </small>
          </div>
          <label
            :disabled="locked || loading || zeroClaim"
            for="invoice"
            class="w-1/3 px-3 leading-none flex items-center justify-center text-2xl font-bold text-white  hover:bg-primary-500 transition-all transition-normal focus:outline-none focus:shadow-outline"
          >
            <template v-if="!invoice">
              +
            </template>
            <icon
              v-else
              icon="check"
              class="w-6 h-6 icons-positive"
            />
            <input
              id="invoice"
              ref="invoice"
              type="file"
              class="sr-only"
              @change="checkInvoice"
            />
          </label>
        </div>
      </div>
      <div class="w-full flex items-center bg-grey-900">
        <div class="px-3 py-2 mr-3">
          <label
            :class="{
              'text-grey-400' : !zeroClaim,
              'text-white' : zeroClaim
            }"
          >
            <input
              v-model="zeroClaim"
              type="checkbox"
              class="mr-2"
            >
            No claim required.
          </label>
        </div>
        <div class="w-1/3 ml-auto px-3 py-2">
          <app-button
            type="submit"
            :disabled="locked || loading || invalidClaimRequest"
            class="button--small"
          >
            <template v-if="loading">
              Submitting...
            </template>
            <template v-else-if="zeroClaim">
              Submit
            </template>
            <template v-else>
              Claim
            </template>
          </app-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { parseISO, format, addMonths } from "date-fns";
export default {
  name: "ClaimsCalculator",
  filters: {
    toPounds(value) {
      if (isNaN(value)) return "----";
      return parseFloat(value).toLocaleString("en-GB", {
        style: "currency",
        currency: "GBP"
      });
    }
  },
  props: {
    competitionProjectPartnerId: {
      type: Number,
      required: true
    },
    canRequest: {
      type: Boolean,
      required: false
    },
    claimPeriods: {
      type: Object,
      required: true
    },
    currentPartner: {
      type: Object,
      required: true
    },
    remaining: {
      type: Number,
      required: false,
      default: 0
    },
    project: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      zeroClaim: false,
      expenditure: {
        labour_costs: 0,
        overheads: 0,
        travel_subsistence: 0,
        software: 0,
        capital_equipment: 0,
        materials: 0,
        sub_contracts: 0
      },
      invoice: null
    };
  },
  computed: {
    hasInvoice() {
      return this.$refs.invoice && this.$refs.invoice.files;
    },
    locked() {
      return (
        !this.claimPeriods.current || this.remaining === 0 || !this.canRequest
      );
    },
    isLast() {
      return !this.claimPeriods.next;
    },
    parseCurrentOpen() {
      if (this.locked) return;
      return parseISO(this.claimPeriods.current.open_date);
    },
    parseNextOpen() {
      return parseISO(this.claimPeriods.next.open_date);
    },
    claimWindowDate() {
      if (this.isLast) return;
      return format(addMonths(this.parseNextOpen, 1), "LL/yy");
    },
    currentClaimDate() {
      return !this.locked
        ? format(this.parseCurrentOpen, "LL/yy")
        : this.claimWindowDate;
    },
    grantInterventionRate() {
      return this.currentPartner.grant_intervention_rate;
    },
    total() {
      return Object.values(this.expenditure)
        .reduce((total, value) => {
          if (isNaN(value) || value < 0) return total;
          return total + parseFloat(value);
        }, 0)
        .toFixed(2);
    },
    grantRequest() {
      if (!this.grantInterventionRate) return this.total;
      return (this.total * (this.grantInterventionRate / 100)).toFixed(2);
    },
    invalidClaimRequest() {
      // Remaining Grant Available
      if(this.zeroClaim) return false;
      return (
        this.grantRequest <= 0 ||
        this.validateSubContracts ||
        !this.invoice ||
        this.overclaimed
      );
    },
    overclaimed() {
      return this.grantRequest > this.remaining;
    },
    validateOverheads() {
      return false;
      // if (this.expenditure.overheads === 0) return false;
      // return this.expenditure.overheads > this.expenditure.labour_costs * 0.5;
    },
    validateSubContracts() {
      return false;
      // if(this.expenditure.sub_contracts === 0) return false
      // return this.expenditure.sub_contracts > (this.total * .2)
    }
  },
  methods: {
    checkInvoice() {
      const invoice = this.$refs.invoice;
      return (this.invoice = invoice);
    },
    getError(key) {
      if (!key) return false;
      return this[key];
    },
    expenditureInfo(key) {
      const lookup = {
        labour_costs: {
          label: "Labour Costs",
          validation: false
        },
        overheads: {
          label: "Overheads",
          validation: "validateOverheads",
          errorMessage: "Overheads are capped at 50% of labour costs."
        },
        travel_subsistence: {
          label: "Travel & Subsistence",
          validation: false
        },
        software: {
          label: "Software",
          validation: false
        },
        capital_equipment: {
          label: "Capital Equipment",
          validation: false
        },
        materials: {
          label: "Materials",
          validation: false
        },
        sub_contracts: {
          label: "Sub Contracts",
          validation: "validateSubContracts",
          errorMessage:
            "Sub Contractors cannot be more than 20% of claim total."
        }
      };
      return lookup[key];
    },
    sendRequest() {
      if (this.loading || this.invalidClaimRequest) return;
      this.loading = true;

      let formData = new FormData();
      const invoice = this.invoice?.files?.[0];
      const values = {
        value: this.grantRequest,
        total: this.total
      };

      formData.append("claim_period", format(this.parseCurrentOpen, "LLyy"));
      formData.append(
        "expenditure",
        JSON.stringify(Object.assign({}, this.expenditure, values))
      );
      formData.append('zero_claim', this.zeroClaim);
      formData.append("invoice", invoice);

      axios
        .post(
          route("projects.claim.request", {
            project: this.project.uuid,
            partner: this.competitionProjectPartnerId
          }),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
        .then(data => this.$emit("claim:submitted", data))
        .catch(err => console.log(err))
        .then(() => (this.loading = false));
    }
  }
};
</script>
